<template>
  <div class="league-data">
    <div class="league-data__header">
      <h1>League Data</h1>
      <p v-if="fetchedAt" class="league-data__status">
        Last updated {{ formatDateTime(fetchedAt) }} — {{ leagues.length }} leagues
      </p>
    </div>

    <div v-if="error" class="league-data__error">{{ error }}</div>

    <!-- Your teams: mapping + summary -->
    <section v-if="isAdmin && internalTeams.length" class="league-data__section">
      <h2>Your teams</h2>
      <div
        v-for="team in internalTeams"
        :key="team.id"
        class="league-data__team-row"
      >
        <div class="team-row__head">
          <strong>{{ team.teamName }}</strong>
          <template v-if="team.mapped_league_id">
            <span class="team-row__league">{{ team.mapped_league_name }}</span>
            <button class="team-row__btn team-row__btn--link" @click="unmap(team)">
              Unlink
            </button>
          </template>
          <template v-else-if="mappingTeamId === team.id">
            <select v-model="mappingLeagueId" @change="loadMappingTeams">
              <option disabled value="">Pick league…</option>
              <option v-for="l in leagues" :key="l.id" :value="l.id">
                {{ l.name }}
              </option>
            </select>
            <select v-model="mappingExternalTeamId">
              <option disabled value="">Which team is yours?</option>
              <option v-for="t in mappingTeams" :key="t.external_team_id" :value="t.external_team_id">
                {{ t.team_name }}
              </option>
            </select>
            <button
              class="team-row__btn"
              :disabled="!mappingLeagueId || !mappingExternalTeamId"
              @click="saveMapping(team)"
            >
              Link
            </button>
            <button class="team-row__btn team-row__btn--link" @click="mappingTeamId = null">
              Cancel
            </button>
          </template>
          <template v-else>
            <span class="team-row__league team-row__league--none">Not linked to a league</span>
            <button class="team-row__btn" @click="startMapping(team)">Link league…</button>
          </template>
        </div>

        <!-- Mapped team summary strip -->
        <div v-if="summaries[team.id] && summaries[team.id].mapped" class="team-row__summary">
          <div class="summary__stat">
            <span class="summary__big">{{ ordinal(summaries[team.id].ladder.position) }}</span>
            <span class="summary__label">of {{ summaries[team.id].ladder.total_teams }}</span>
          </div>
          <div class="summary__stat">
            <span class="summary__big">{{ summaries[team.id].ladder.points }}</span>
            <span class="summary__label">points</span>
          </div>
          <div class="summary__form">
            <span
              v-for="(r, i) in [...summaries[team.id].last_results].reverse()"
              :key="i"
              class="form-badge"
              :class="'form-badge--' + r.outcome.toLowerCase()"
              :title="r.home_team + ' ' + r.home_score + '–' + r.away_score + ' ' + r.away_team"
            >
              {{ r.outcome }}
            </span>
          </div>
          <div v-if="summaries[team.id].next_fixture" class="summary__next">
            <span class="summary__label">Next:</span>
            {{ opponentOf(summaries[team.id]) }}
            · {{ formatDate(summaries[team.id].next_fixture.kickoff_utc) }}
            {{ formatKickoff(summaries[team.id].next_fixture.kickoff_utc) }}
            <template v-if="summaries[team.id].next_fixture.ground_name">
              · {{ summaries[team.id].next_fixture.ground_name }}
            </template>
          </div>
        </div>
      </div>
    </section>

    <!-- League browser -->
    <div v-if="leagues.length" class="league-data__controls">
      <label for="league-select">League</label>
      <select id="league-select" v-model="selectedLeagueId" @change="loadLeague">
        <option v-for="l in leagues" :key="l.id" :value="l.id">
          {{ l.name }} ({{ l.competition_name }})
        </option>
      </select>
    </div>

    <div v-if="loading" class="league-data__loading">Loading…</div>

    <div v-if="!loading && selectedLeagueId" class="league-data__sections">
      <section class="league-data__section">
        <h2>Ladder</h2>
        <div class="league-data__table-scroll">
          <table class="league-data__table">
            <thead>
              <tr>
                <th>Pos</th>
                <th class="team-col">Team</th>
                <th>P</th>
                <th>W</th>
                <th>D</th>
                <th>L</th>
                <th>GF</th>
                <th>GA</th>
                <th>GD</th>
                <th>Pts</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in ladder"
                :key="row.external_team_id"
                :class="{ 'own-team': isOwnTeam(row.club_name) }"
              >
                <td>{{ row.position }}</td>
                <td class="team-col">
                  <img v-if="row.logo_url" :src="row.logo_url" alt="" class="club-logo" />
                  {{ row.team_name }}
                </td>
                <td>{{ row.played }}</td>
                <td>{{ row.won }}</td>
                <td>{{ row.drawn }}</td>
                <td>{{ row.lost }}</td>
                <td>{{ row.goals_for }}</td>
                <td>{{ row.goals_against }}</td>
                <td>{{ row.goal_difference }}</td>
                <td class="points">{{ row.points }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="league-data__section">
        <h2>Teams in this league</h2>
        <div class="league-data__teams-grid">
          <div
            v-for="row in ladder"
            :key="'team-' + row.external_team_id"
            class="league-data__team-card"
            :class="{ 'own-team': isOwnTeam(row.club_name) }"
          >
            <img v-if="row.logo_url" :src="row.logo_url" alt="" class="club-logo club-logo--lg" />
            <div>
              <div class="team-card__club">{{ row.club_name }}</div>
              <div class="team-card__team">{{ row.team_name }}</div>
            </div>
          </div>
        </div>
      </section>

      <section class="league-data__section">
        <h2>Results</h2>
        <div v-if="!results.length" class="league-data__empty">No completed games yet.</div>
        <div
          v-for="fx in results"
          :key="fx.id"
          class="league-data__fixture league-data__fixture--result"
        >
          <span class="fixture__round">{{ fx.round_label }}</span>
          <span class="fixture__team fixture__team--home">
            {{ fx.home_team }}
            <img v-if="fx.home_logo" :src="fx.home_logo" alt="" class="club-logo" />
          </span>
          <span class="fixture__score">{{ fx.home_score }} – {{ fx.away_score }}</span>
          <span class="fixture__team">
            <img v-if="fx.away_logo" :src="fx.away_logo" alt="" class="club-logo" />
            {{ fx.away_team }}
          </span>
          <span class="fixture__meta">{{ formatDate(fx.kickoff_utc) }}</span>
        </div>
      </section>

      <section class="league-data__section">
        <h2>Upcoming fixtures</h2>
        <div v-if="!upcoming.length" class="league-data__empty">No upcoming games.</div>
        <div v-for="fx in upcoming" :key="fx.id" class="league-data__fixture">
          <span class="fixture__round">{{ fx.round_label }}</span>
          <span class="fixture__team fixture__team--home">
            {{ fx.home_team }}
            <img v-if="fx.home_logo" :src="fx.home_logo" alt="" class="club-logo" />
          </span>
          <span class="fixture__score fixture__score--time">
            {{ formatKickoff(fx.kickoff_utc) }}
          </span>
          <span class="fixture__team">
            <img v-if="fx.away_logo" :src="fx.away_logo" alt="" class="club-logo" />
            {{ fx.away_team }}
          </span>
          <span class="fixture__meta">
            {{ formatDate(fx.kickoff_utc) }}
            <template v-if="fx.ground_name"> · {{ fx.ground_name }}</template>
          </span>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { ExternalLeagues } from "@/services/external-leagues-service.js";
import tenantConfig from "@/config/tenant";

// Kickoffs are stored UTC; games are played in South Australia.
const DISPLAY_TZ = "Australia/Adelaide";

export default {
  name: "LeagueData",
  data() {
    return {
      leagues: [],
      internalTeams: [],
      summaries: {},
      selectedLeagueId: "",
      ladder: [],
      fixtures: [],
      loading: false,
      error: null,
      mappingTeamId: null,
      mappingLeagueId: "",
      mappingExternalTeamId: "",
      mappingTeams: [],
    };
  },
  computed: {
    ...mapGetters(["getProfile"]),
    clubId() {
      return this.getProfile?.clubId;
    },
    isAdmin() {
      return this.getProfile?.userType === "Admin";
    },
    fetchedAt() {
      return this.leagues[0]?.fetched_at || null;
    },
    results() {
      return this.fixtures
        .filter((f) => f.status === "complete")
        .sort((a, b) => new Date(b.kickoff_utc) - new Date(a.kickoff_utc));
    },
    upcoming() {
      return this.fixtures
        .filter((f) => f.status !== "complete")
        .sort((a, b) => new Date(a.kickoff_utc) - new Date(b.kickoff_utc));
    },
  },
  async mounted() {
    if (!this.clubId) return;
    try {
      const [leaguesRes, teamsRes] = await Promise.all([
        ExternalLeagues.getLeagues(this.clubId),
        ExternalLeagues.getInternalTeams(this.clubId),
      ]);
      this.leagues = leaguesRes.data || [];
      this.internalTeams = teamsRes.data || [];
      if (this.leagues.length) {
        this.selectedLeagueId = this.leagues[0].id;
        await this.loadLeague();
      }
      await this.loadSummaries();
    } catch (e) {
      this.error =
        "Couldn't load league data. Has the fetch pipeline run? (" + e.message + ")";
    }
  },
  methods: {
    async loadLeague() {
      this.loading = true;
      this.error = null;
      try {
        const [ladderRes, fixturesRes] = await Promise.all([
          ExternalLeagues.getLadder(this.clubId, this.selectedLeagueId),
          ExternalLeagues.getFixtures(this.clubId, this.selectedLeagueId),
        ]);
        this.ladder = ladderRes.data || [];
        this.fixtures = fixturesRes.data || [];
      } catch (e) {
        this.error = "Couldn't load this league. (" + e.message + ")";
        this.ladder = [];
        this.fixtures = [];
      } finally {
        this.loading = false;
      }
    },
    async loadSummaries() {
      const mapped = this.internalTeams.filter((t) => t.mapped_league_id);
      const entries = await Promise.all(
        mapped.map(async (t) => {
          const res = await ExternalLeagues.getTeamSummary(this.clubId, t.id).catch(
            () => null,
          );
          return [t.id, res?.data || null];
        }),
      );
      this.summaries = Object.fromEntries(entries.filter(([, v]) => v));
    },
    startMapping(team) {
      this.mappingTeamId = team.id;
      this.mappingLeagueId = "";
      this.mappingExternalTeamId = "";
      this.mappingTeams = [];
    },
    async loadMappingTeams() {
      this.mappingExternalTeamId = "";
      if (!this.mappingLeagueId) return;
      const res = await ExternalLeagues.getLadder(this.clubId, this.mappingLeagueId);
      this.mappingTeams = res.data || [];
      // Pre-select the team whose club name matches ours
      const own = this.mappingTeams.find((t) => this.isOwnTeam(t.club_name));
      if (own) this.mappingExternalTeamId = own.external_team_id;
    },
    async saveMapping(team) {
      await ExternalLeagues.mapTeam(
        this.clubId,
        team.id,
        this.mappingLeagueId,
        this.mappingExternalTeamId,
      );
      this.mappingTeamId = null;
      await this.refreshTeams();
    },
    async unmap(team) {
      await ExternalLeagues.unmapTeam(this.clubId, team.id);
      await this.refreshTeams();
    },
    async refreshTeams() {
      const res = await ExternalLeagues.getInternalTeams(this.clubId);
      this.internalTeams = res.data || [];
      await this.loadSummaries();
    },
    opponentOf(summary) {
      const next = summary.next_fixture;
      if (!next) return "";
      const us = summary.external_team_name;
      // (H)/(A) is OUR venue: opponent is the away team when we're at home.
      return next.home_team === us ? next.away_team + " (H)" : next.home_team + " (A)";
    },
    isOwnTeam(clubName) {
      const club = (tenantConfig.name || "").toLowerCase();
      const candidate = (clubName || "").toLowerCase();
      if (!club || !candidate) return false;
      return candidate.includes(club.split(" ")[0]);
    },
    ordinal(n) {
      if (!n) return "–";
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    },
    formatDate(utc) {
      if (!utc) return "";
      return new Date(utc.replace(" ", "T") + (utc.includes("Z") ? "" : "Z")).toLocaleDateString(
        "en-AU",
        { timeZone: DISPLAY_TZ, weekday: "short", day: "numeric", month: "short" },
      );
    },
    formatKickoff(utc) {
      if (!utc) return "";
      return new Date(utc.replace(" ", "T") + (utc.includes("Z") ? "" : "Z")).toLocaleTimeString(
        "en-AU",
        { timeZone: DISPLAY_TZ, hour: "2-digit", minute: "2-digit" },
      );
    },
    formatDateTime(utc) {
      if (!utc) return "";
      return new Date(utc.replace(" ", "T") + (utc.includes("Z") ? "" : "Z")).toLocaleString(
        "en-AU",
        { timeZone: DISPLAY_TZ, day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" },
      );
    },
  },
};
</script>

<style scoped lang="scss">
.league-data {
  padding: 24px;
  max-width: 1100px;
  margin: 0 auto;

  &__header h1 {
    margin-bottom: 4px;
  }

  &__status {
    color: #6c757d;
    font-size: 0.85rem;
  }

  &__error {
    background: #f8d7da;
    color: #721c24;
    padding: 12px 16px;
    border-radius: 6px;
    margin: 12px 0;
  }

  &__team-row {
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 10px 14px;
    margin-bottom: 10px;

    .team-row__head {
      display: flex;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;
    }

    .team-row__league {
      color: #198754;
      font-size: 0.85rem;

      &--none {
        color: #6c757d;
      }
    }

    .team-row__btn {
      border: 1px solid #ced4da;
      background: #fff;
      color: #212529;
      border-radius: 6px;
      padding: 4px 12px;
      font-size: 0.8rem;
      cursor: pointer;

      &:disabled {
        opacity: 0.5;
        cursor: default;
      }

      &--link {
        border: none;
        color: #6c757d;
        text-decoration: underline;
      }
    }

    select {
      padding: 4px 8px;
      border: 1px solid #ced4da;
      border-radius: 6px;
      font-size: 0.85rem;
      max-width: 280px;
    }
  }

  .team-row__summary {
    display: flex;
    align-items: center;
    gap: 20px;
    flex-wrap: wrap;
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px dashed #e9ecef;

    .summary__stat {
      display: flex;
      align-items: baseline;
      gap: 5px;
    }

    .summary__big {
      font-size: 1.3rem;
      font-weight: 700;
    }

    .summary__label {
      color: #6c757d;
      font-size: 0.8rem;
    }

    .summary__form {
      display: flex;
      gap: 4px;
    }

    .form-badge {
      width: 22px;
      height: 22px;
      border-radius: 50%;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 0.7rem;
      font-weight: 700;
      color: #fff;

      &--w {
        background: #198754;
      }

      &--d {
        background: #adb5bd;
      }

      &--l {
        background: #dc3545;
      }
    }

    .summary__next {
      font-size: 0.85rem;
    }
  }

  &__controls {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 16px 0 24px;

    label {
      font-weight: 600;
      margin: 0;
    }

    select {
      padding: 8px 12px;
      border: 1px solid #ced4da;
      border-radius: 6px;
      min-width: 320px;
      max-width: 100%;
    }
  }

  &__loading,
  &__empty {
    color: #6c757d;
    padding: 12px 0;
  }

  &__section {
    margin-bottom: 32px;

    h2 {
      font-size: 1.1rem;
      margin-bottom: 12px;
      border-bottom: 2px solid #e9ecef;
      padding-bottom: 6px;
    }
  }

  &__table-scroll {
    overflow-x: auto;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;

    th,
    td {
      padding: 8px 10px;
      text-align: center;
      border-bottom: 1px solid #e9ecef;
      white-space: nowrap;
    }

    .team-col {
      text-align: left;
      min-width: 220px;
    }

    .points {
      font-weight: 700;
    }

    tr.own-team {
      background: #fff8e1;
      font-weight: 600;
    }
  }

  &__teams-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
    gap: 12px;
  }

  &__team-card {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border: 1px solid #e9ecef;
    border-radius: 8px;

    &.own-team {
      border-color: #ffc107;
      background: #fff8e1;
    }

    .team-card__club {
      font-weight: 600;
      font-size: 0.9rem;
    }

    .team-card__team {
      color: #6c757d;
      font-size: 0.8rem;
    }
  }

  &__fixture {
    display: grid;
    grid-template-columns: 44px 1fr 84px 1fr auto;
    align-items: center;
    gap: 8px;
    padding: 8px 4px;
    border-bottom: 1px solid #f1f3f5;
    font-size: 0.9rem;

    .fixture__round {
      color: #6c757d;
      font-size: 0.8rem;
    }

    .fixture__team {
      display: flex;
      align-items: center;
      gap: 6px;

      &--home {
        justify-content: flex-end;
        text-align: right;
      }
    }

    .fixture__score {
      text-align: center;
      font-weight: 700;
      background: #f1f3f5;
      border-radius: 6px;
      padding: 4px 0;

      &--time {
        font-weight: 500;
        color: #495057;
      }
    }

    .fixture__meta {
      color: #6c757d;
      font-size: 0.8rem;
      white-space: nowrap;
    }
  }

  .club-logo {
    width: 20px;
    height: 20px;
    object-fit: contain;

    &--lg {
      width: 34px;
      height: 34px;
    }
  }

  @media (max-width: 640px) {
    &__fixture {
      grid-template-columns: 36px 1fr 64px 1fr;

      .fixture__meta {
        grid-column: 1 / -1;
        text-align: center;
      }
    }
  }
}
</style>
